import { useEffect, useState, type RefObject } from 'react'
import { useLocation } from 'react-router'
import { isIosApp } from '@/lib/platform'
import { canControlNativeSwipeBack, setNativeSwipeBack } from '@/lib/nativeSwipeBack'

/** How close to the left edge a swipe has to start. */
export const EDGE_WIDTH_PX = 24
/** How far right it has to travel before the menu opens. */
export const OPEN_DISTANCE_PX = 40
/** The drawer is only a phone-width surface; from `lg` the sidebar is always shown. */
const DESKTOP_QUERY = '(min-width: 1024px)'

/**
 * Owner, 2026-09-29: in the iOS app, a swipe from the left edge on a page the
 * menu links to opens the menu and leaves it open. It used to run the native
 * swipe-back (EP-122) instead, which on those pages only went to whatever
 * page came before. Every other page keeps swiping back.
 *
 * "A page the menu links to" is read from the menu itself: the mobile drawer
 * stays mounted, so the page counts when the drawer holds a link to exactly
 * this path. That follows role and module gating with no second list to keep
 * in step with the sidebar.
 *
 * Runs only in an iOS app build that can switch the native gesture off
 * (`canControlNativeSwipeBack`). Elsewhere the edge belongs to the system:
 * mobile Safari and older app builds swipe back, Android's edge is its own
 * back gesture. Opening the menu there as well would do both at once.
 */
export function useMenuEdgeSwipe({
  drawerRef,
  open,
  onOpen,
}: {
  drawerRef: RefObject<HTMLElement | null>
  open: boolean
  onOpen: () => void
}) {
  const { pathname } = useLocation()
  const [isMenuPage, setIsMenuPage] = useState(false)
  const active = isIosApp() && canControlNativeSwipeBack()

  // Re-checked when the drawer's links change, since gated entries appear once
  // flags and settings load.
  useEffect(() => {
    const drawer = drawerRef.current
    if (!drawer) return
    const check = () =>
      setIsMenuPage(Array.from(drawer.querySelectorAll('a[href]')).some((a) => a.getAttribute('href') === pathname))
    check()
    const observer = new MutationObserver(check)
    observer.observe(drawer, { childList: true, subtree: true, attributes: true, attributeFilter: ['href'] })
    return () => observer.disconnect()
  }, [drawerRef, pathname])

  // Off on a menu page, and off whenever the menu is open, so the swipe that
  // opened it cannot also go back.
  useEffect(() => {
    if (!active) return
    setNativeSwipeBack(!isMenuPage && !open)
  }, [active, isMenuPage, open])

  useEffect(() => {
    if (!active || !isMenuPage || open) return
    if (typeof window.matchMedia === 'function' && window.matchMedia(DESKTOP_QUERY).matches) return

    let start: { x: number; y: number } | null = null

    const onStart = (event: TouchEvent) => {
      const touch = event.touches[0]
      start = touch && touch.clientX <= EDGE_WIDTH_PX ? { x: touch.clientX, y: touch.clientY } : null
    }
    const onMove = (event: TouchEvent) => {
      const touch = event.touches[0]
      if (!start || !touch) return
      const dx = touch.clientX - start.x
      const dy = Math.abs(touch.clientY - start.y)
      // A scroll that happened to start at the edge is not a swipe.
      if (dy > Math.max(dx, 16)) {
        start = null
        return
      }
      if (dx >= OPEN_DISTANCE_PX) {
        start = null
        onOpen()
      }
    }
    const onEnd = () => {
      start = null
    }

    document.addEventListener('touchstart', onStart, { passive: true })
    document.addEventListener('touchmove', onMove, { passive: true })
    document.addEventListener('touchend', onEnd, { passive: true })
    document.addEventListener('touchcancel', onEnd, { passive: true })
    return () => {
      document.removeEventListener('touchstart', onStart)
      document.removeEventListener('touchmove', onMove)
      document.removeEventListener('touchend', onEnd)
      document.removeEventListener('touchcancel', onEnd)
    }
  }, [active, isMenuPage, open, onOpen])
}
